import { Fragment, useCallback, useEffect, useRef } from 'react';
import {
  type Control,
  FieldPath,
  FieldValues,
  type FormState,
  type UseFormSetError,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import { CheckboxField, DatePickerField, SelectField, TextInputField } from '@brighte/form-components';
import { Address } from '@components/Address';
import { Text } from '@spark-web/text';
import FieldArray from '../../components/FieldArray/FieldArray';
import { useApplicationData } from '../../provider';
import { LABELS, OWNERSHIP_DETAILS } from '../../utils';
import SmeDriverLicenceFields from './SmeDriverLicenceFields';
import { generateOptionsFromMaxCount } from './helpers';

const MAX_DIRECTORS = 10;

const fieldArrayDefaultWithLicence = {
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  phone: '',
  drivingLicense: { number: '', cardNumber: '', state: undefined, expiryDate: '' },
  dateOfBirth: '',
  address: null,
  privacyAndCreditStatement: '',
};

const fieldArrayDefaultBasic = {
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  phone: '',
  dateOfBirth: '',
  address: null,
  privacyAndCreditStatement: '',
};

type SmeDirectorProps<TFieldValues extends FieldValues> = {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  watch: UseFormWatch<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  formState: FormState<TFieldValues>;
  setError: UseFormSetError<TFieldValues>;
  showLicence: boolean;
  dropdownName: string;
  showDirectors?: boolean;
  roleLabel?: string;
};

function SmeDirector<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  watch,
  setValue,
  formState,
  setError,
  showLicence,
  dropdownName,
  showDirectors = true,
  roleLabel = 'Director',
}: Readonly<SmeDirectorProps<TFieldValues>>) {
  const {
    FIRST_NAME,
    MIDDLE_NAME,
    LAST_NAME,
    DATE_OF_BIRTH,
    HOME_ADDRESS,
    EMAIL_ADDRESS,
    PHONE_NUMBER,
  } = LABELS;

  const roleLabelLower = roleLabel.toLowerCase();
  const numberOfLabel = `Number of ${roleLabel}s`;
  const rolePlaceholder = `Select number of ${roleLabelLower}s`;
  const roleDescription = `Please select how many ${roleLabelLower}s and provide their names`;


  const directorList = watch(name);

  const { BusinessDetails: businessDetails, ContactDetails: contactDetails } = useApplicationData([
    'BusinessDetails',
    'ContactDetails',
  ]);
  const baseDefaults = showLicence ? fieldArrayDefaultWithLicence : fieldArrayDefaultBasic;

  const getDefaultValues = useCallback(
    (index: number) => {
      if (index === 0) {
        return {
          ...baseDefaults,
          firstName: businessDetails?.applicant?.firstName ?? '',
          middleName: businessDetails?.applicant?.middleName ?? '',
          lastName: businessDetails?.applicant?.lastName ?? '',
          email: contactDetails?.email ?? '',
          phone: contactDetails?.mobile ?? '',
        };
      }
      return baseDefaults;
    },
    [baseDefaults, businessDetails, contactDetails],
  );

  const addressDefaultsRef = useRef<Map<string, any>>(new Map());

  const errors = formState?.errors;

  useEffect(() => {
    const directorsError = name
      .split('.')
      .reduce<Record<string, any> | undefined>((errorObj, key) => errorObj?.[key], errors as Record<string, any>);
    if (directorsError?.message) {
      setError(dropdownName as FieldPath<TFieldValues>, directorsError);
    } else if (directorsError?.root?.message) {
      setError(dropdownName as FieldPath<TFieldValues>, directorsError.root);
    }
  }, [errors, name, setError, dropdownName]);

  const initialDirectorListRef = useRef(directorList);

  useEffect(() => {
    if (initialDirectorListRef.current?.length > 0) {
      setValue(dropdownName as FieldPath<TFieldValues>, initialDirectorListRef.current.length);
    }
  }, [setValue, dropdownName]);

  return (
    <FieldArray name={name} control={control} defaultValues={getDefaultValues}>
      {({ handleChange, fields }) => {
        return (
          <>
            <SelectField
              name={dropdownName as FieldPath<TFieldValues>}
              control={control}
              label={numberOfLabel}
              options={generateOptionsFromMaxCount(MAX_DIRECTORS, 1)}
              fieldProps={{
                description: roleDescription,
              }}
              onChange={handleChange}
              placeholder={fields.length > 0 ? undefined : rolePlaceholder}
            />
            {fields.length > 0 && showDirectors
              ? fields.map((item, index) => {
                  const isFirstDirector = index === 0;

                  if (!addressDefaultsRef.current.has(item.id)) {
                    addressDefaultsRef.current.set(item.id, directorList?.[index]?.address ?? null);
                  }

                  return (
                    <Fragment key={item.id}>
                      <Text tone="muted" weight="semibold">
                        {`${roleLabel} #${index + 1}`}
                      </Text>

                      <TextInputField
                        name={`${name}.${index}.firstName` as FieldPath<TFieldValues>}
                        label={FIRST_NAME}
                        control={control}
                        maxLength={255}
                        FieldProps={{ readOnly: isFirstDirector }}
                      />
                      <TextInputField
                        name={`${name}.${index}.middleName` as FieldPath<TFieldValues>}
                        label={MIDDLE_NAME}
                        control={control}
                        maxLength={255}
                        FieldProps={{ secondaryLabel: '(Optional)', readOnly: isFirstDirector }}
                      />
                      <TextInputField
                        name={`${name}.${index}.lastName` as FieldPath<TFieldValues>}
                        label={LAST_NAME}
                        control={control}
                        maxLength={255}
                        FieldProps={{ readOnly: isFirstDirector }}
                      />

                      <TextInputField
                        name={`${name}.${index}.email` as FieldPath<TFieldValues>}
                        label={EMAIL_ADDRESS}
                        control={control}
                        maxLength={255}
                        FieldProps={{ readOnly: isFirstDirector && !!contactDetails }}
                      />
                      <TextInputField
                        name={`${name}.${index}.phone` as FieldPath<TFieldValues>}
                        label={PHONE_NUMBER}
                        control={control}
                        FieldProps={{ readOnly: isFirstDirector && !!contactDetails }}
                      />

                      {showLicence ? (
                        <SmeDriverLicenceFields
                          name={`${name}.${index}` as FieldPath<TFieldValues>}
                          control={control}
                          watch={watch}
                        />
                      ) : (
                        <DatePickerField
                          name={`${name}.${index}.dateOfBirth` as FieldPath<TFieldValues>}
                          control={control}
                          label={DATE_OF_BIRTH}
                          autoComplete="off"
                        />
                      )}

                      <Address
                        control={control}
                        label={HOME_ADDRESS}
                        name={`${name}.${index}.address` as FieldPath<TFieldValues>}
                        setValue={setValue}
                        defaultValue={addressDefaultsRef.current.get(item.id)}
                      />

                      <CheckboxField
                        name={`${name}.${index}.privacyAndCreditStatement` as FieldPath<TFieldValues>}
                        control={control}
                        checkedValue={OWNERSHIP_DETAILS.PRIVACY_AND_CREDIT_TEXT}
                      >
                        {OWNERSHIP_DETAILS.PRIVACY_AND_CREDIT_STATEMENT}
                      </CheckboxField>
                    </Fragment>
                  );
                })
              : null}
          </>
        );
      }}
    </FieldArray>
  );
}

export default SmeDirector;
